import { useState } from "react";
import type { JobIssueDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { formatDateTime } from "../../lib/format.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n, type MessageKey } from "../../i18n/index.js";
import { Plus } from "lucide-react";

export function IssuePanel({
  jobId,
  issues,
  onChanged,
}: {
  jobId: string;
  issues: JobIssueDto[];
  onChanged(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [raising, setRaising] = useState(false);
  const [form, setForm] = useState({ description: "", severity: "medium" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.raiseJobIssue(jobId, form);
      push(t("jobs.issues.raisedToast"));
      setForm({ description: "", severity: "medium" });
      setRaising(false);
      onChanged();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  const resolve = async (issueId: string) => {
    setBusy(true);
    try {
      await api.resolveJobIssue(jobId, issueId);
      push(t("jobs.issues.resolvedToast"));
      onChanged();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">{t("jobs.issues.title")}</h2>
        <Button size="sm" variant="secondary" onClick={() => setRaising((v) => !v)}>
          <Plus className="h-4 w-4" aria-hidden="true" /> {t("jobs.issues.raise")}
        </Button>
      </div>
      {issues.length === 0 ? (
        <p className="text-sm text-ink-muted">{t("jobs.issues.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {issues.map((issue) => (
            <li key={issue.id} className="rounded border border-line px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-ink">{issue.description}</p>
                <Badge tone={issue.status === "open" ? "risk" : "complete"}>
                  {t(`jobs.issues.status.${issue.status}` as MessageKey)}
                </Badge>
              </div>
              <p className="text-xs text-ink-muted">
                {t(`status.severity.${issue.severity}` as MessageKey)} ·{" "}
                {issue.raisedByName}
                {issue.resolvedAt
                  ? ` · ${t("jobs.issues.resolvedAt", { date: formatDateTime(issue.resolvedAt) })}`
                  : ""}
              </p>
              {issue.status === "open" ? (
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-1"
                  disabled={busy}
                  onClick={() => void resolve(issue.id)}
                >
                  {t("jobs.issues.resolve")}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {raising ? (
        <div className="mt-3 space-y-2 border-t border-line pt-3">
          <TextInput
            label={t("jobs.issues.whatsWrong")}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <SelectInput
            label={t("jobs.issues.severity")}
            options={[
              { value: "low", label: t("status.severity.low") },
              { value: "medium", label: t("status.severity.medium") },
              { value: "high", label: t("status.severity.high") },
            ]}
            value={form.severity}
            onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
          />
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={submit}
              disabled={busy || !form.description.trim()}
            >
              {t("jobs.issues.save")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setRaising(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
